/**
 * Cosmeau V6 cart helper
 * Ensures product header ATC uses AJAX + Corner Cart instead of native /cart navigation.
 */
(function () {
  if (window.CosmeauV6Cart && window.CosmeauV6Cart.__ready) return;

  const api = (window.CosmeauV6Cart = window.CosmeauV6Cart || {});
  const BOUND = 'cv6CornerCartBound';

  function cartAddUrl() {
    const base = (window.routes && window.routes.cart_add_url) || '/cart/add';
    return /\.js$/i.test(base) ? base : base + '.js';
  }

  function resolveSellingPlan(form, body) {
    const hidden = form.querySelector('[name="selling_plan"]');
    let planId = String(body.get('selling_plan') || hidden?.value || '').trim();
    const widget = form.closest('[data-cv6-default-root], [data-cv6-g6-pdp], [data-v6wp]')?.querySelector('#juo-subscription-widget, juo-subscription-widget') || document.querySelector('#juo-subscription-widget, juo-subscription-widget');
    const subscriptionMode = widget?.querySelector('input[name="mode"][value="subscription"]');
    const planSelect = widget?.querySelector('select[data-ref="selling-plan"]');
    if (subscriptionMode?.checked && planSelect?.value) planId = String(planSelect.value);
    if (planId) body.set('selling_plan', planId);
    else body.delete('selling_plan');
    if (hidden) hidden.value = planId;
    return planId;
  }

  function isCornerOpen() {
    const nativeDrawer = document.querySelector('cart-drawer[data-cosmeau-cart]');
    if (nativeDrawer) return nativeDrawer.classList.contains('active');
    const primary = document.getElementById('corner-cowi-open-primary-card');
    if (!primary) return false;
    try {
      return getComputedStyle(primary).opacity === '1';
    } catch (_) {
      return String(primary.className || '').indexOf('opacity-100') !== -1;
    }
  }

  function openCornerCart(params) {
    // Prefer the native Cosmeau drawer whenever it is mounted. The Corner
    // runtime may still exist after its app embed is disabled and must not
    // intercept DEV carts or redirect customers to /cart.
    const drawer = document.querySelector('cart-drawer[data-cosmeau-cart]');
    if (drawer) {
      if (typeof drawer.open === 'function') {
        try {
          drawer.open();
          return true;
        } catch (_) {}
      }
      // CosmeauCart owns this page. Never fall through to Shopify's default
      // openCart action, because that action intentionally navigates to /cart.
      return false;
    }

    try {
      if (window.corner && typeof window.corner.do === 'function') {
        window.corner.do('openCart', params);
        return true;
      }
    } catch (_) {}

    try {
      if (window.Shopify && window.Shopify.actions && window.Shopify.actions.openCart) {
        window.Shopify.actions.openCart();
        return true;
      }
    } catch (_) {}

    return false;
  }

  function ensureOpen(retryMs) {
    const delays = Array.isArray(retryMs) ? retryMs : [0, 80, 250, 700, 1400];
    delays.forEach(function (ms) {
      setTimeout(function () {
        if (!isCornerOpen()) openCornerCart();
      }, ms);
    });
  }

  async function refreshAndOpen() {
    const cart = window.CosmeauCartDrawer;
    if (cart && typeof cart.refreshSections === 'function') {
      ensureOpen();
      await cart.refreshSections(true);
      return true;
    }
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (_) {}
    ensureOpen();
    return true;
  }

  async function applyCampaignFromForm(form, body) {
    const code = form.querySelector('[data-cv6-campaign-code]')?.value || '';
    const sellingPlan = String(body.get('selling_plan') || '').trim();
    if (!code || sellingPlan) return false;
    try {
      // Shopify's AJAX cart update endpoint does not accept discount codes.
      // The canonical /discount route stores the code for checkout; its redirect
      // response may be non-2xx in fetch while the discount session is still set.
      await fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart', {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'follow',
        cache: 'no-store',
      });
      await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _cosmeau_campaign: code } }),
      });
      return true;
    } catch (_) {
      return false;
    }
  }

  function primeCampaignOnAtcClick(event) {
    const button = event.target?.closest?.(
      'button[type="submit"], button[name="add"], [data-atc], .product-form__submit'
    );
    const form = button?.form || button?.closest?.('form');
    if (!form) return;
    const code = form.querySelector('[data-cv6-campaign-code]')?.value || '';
    const body = new FormData(form);
    const sellingPlan = resolveSellingPlan(form, body);
    if (!code || sellingPlan) return;
    // Corner Cart can intercept the click before the native submit event. Prime
    // Shopify's discount session in capture phase without blocking its ATC flow.
    fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart', {
      method: 'GET',
      credentials: 'same-origin',
      redirect: 'follow',
      cache: 'no-store',
    }).catch(function () {});
  }

  function pendingProductFromForm(form) {
    const scope = form.closest('[data-cv6-default-root], [data-cv6-g6-pdp], [data-v6wp], .product, main') || document;
    const title = scope.querySelector('h1, [data-product-title], .product__title')?.textContent?.trim()
      || document.querySelector('h1')?.textContent?.trim()
      || 'Product';
    const image = scope.querySelector('[data-cv6-g6-main-image], [data-product-featured-media] img, .product__media img, img');
    const variants = Array.from(form.querySelectorAll('select[name^="options"] option:checked, input[name^="options"]:checked'))
      .map((option) => option.textContent?.trim() || option.value)
      .filter(Boolean);
    return {
      title,
      variantTitle: variants.join(' · '),
      imageUrl: image?.currentSrc || image?.src || '',
      quantity: Number(form.querySelector('[name="quantity"]')?.value || 1),
    };
  }

  async function addFromForm(form, options) {
    options = options || {};
    if (!form) throw new Error('Missing product form');

    const button =
      options.button ||
      form.querySelector('[type="submit"][name="add"], [data-atc], [data-v6wp-submit], .product-form__submit, button[name="add"]');

    if (button && (button.disabled || button.getAttribute('aria-disabled') === 'true')) {
      return null;
    }

    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');
    }

    try {
      const body = new FormData(form);
      if (!body.get('id')) throw new Error('Geen variant geselecteerd');
      resolveSellingPlan(form, body);

      const nativeDrawer = document.querySelector('cart-drawer[data-cosmeau-cart]');
      if (nativeDrawer && typeof nativeDrawer.getSectionsToRender === 'function') {
        // Match Corner's perceived performance: show the chosen product in the
        // existing shell immediately, then reconcile with Shopify's response.
        window.CosmeauCartDrawer?.showPendingItem?.(pendingProductFromForm(form));
        nativeDrawer.classList.add('is-cart-loading');
        nativeDrawer.open(button || document.activeElement);
        body.append(
          'sections',
          nativeDrawer.getSectionsToRender().map(function (section) { return section.id; }).join(',')
        );
        body.append('sections_url', window.location.pathname);
        if (typeof nativeDrawer.setActiveElement === 'function') {
          nativeDrawer.setActiveElement(document.activeElement);
        }
      }

      const response = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: body,
        credentials: 'same-origin',
      });

      let data = null;
      try {
        data = await response.json();
      } catch (_) {
        data = {};
      }

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Toevoegen aan winkelmand mislukt');
      }

      const campaignPromise = applyCampaignFromForm(form, body);
      if (nativeDrawer && data.sections && data.sections['cart-drawer']) {
        nativeDrawer.renderContents(data);
      } else {
        await refreshAndOpen();
      }
      window.CosmeauCartDrawer?.clearPendingItem?.();
      const campaignApplied = await campaignPromise;
      if (
        campaignApplied &&
        window.CosmeauCartDrawer &&
        typeof window.CosmeauCartDrawer.refreshSections === 'function'
      ) {
        await window.CosmeauCartDrawer.refreshSections(true);
      }
      if (typeof options.onSuccess === 'function') options.onSuccess(data);
      return data;
    } catch (error) {
      window.CosmeauCartDrawer?.clearPendingItem?.(true);
      if (typeof options.onError === 'function') options.onError(error);
      throw error;
    } finally {
      document.querySelector('cart-drawer[data-cosmeau-cart]')?.classList.remove('is-cart-loading');
      if (button) {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      }
    }
  }

  function onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    // Only standard single-line PDP header forms. Multi-line configurators
    // (G6 / Geurparels) open Corner Cart in their own AJAX handlers.
    if (
      !form.matches(
        'form[data-type="add-to-cart-form"], form[data-product-form].cv6-default-product-form, form.cv6-default-product-form, form.v6wp-hero__form, form[data-product-form][data-cv6-product-form-host]'
      )
    ) {
      // Also allow plain data-product-form wasparfum / default shells without host attr.
      const isWasparfum = form.matches('form.v6wp-hero__form, form[data-product-form].v6wp-hero__form');
      const isDefault = form.matches('form.cv6-default-product-form, form[data-type="add-to-cart-form"]');
      const isPlainProductForm =
        form.matches('form[data-product-form]') &&
        !form.matches('[data-cv6-g6-product-form], form[data-cv6-skip-corner-bind]') &&
        !form.closest('[data-v6gp], .v6gp, [data-cv6-g6-pdp]');
      if (!(isWasparfum || isDefault || isPlainProductForm)) return;
    }
    if (form.matches('[data-cv6-g6-product-form], form[data-cv6-skip-corner-bind]')) return;
    const nativeCosmeauDrawer = document.querySelector('cart-drawer[data-cosmeau-cart]');
    if (form.closest('[data-v6gp], .v6gp, [data-cv6-g6-pdp]') && !nativeCosmeauDrawer) return;

    // Capture-phase stop: never allow native navigation to /cart/add → /cart.
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

    addFromForm(form, {
      button: form.querySelector('[type="submit"][name="add"], [data-atc], [data-v6wp-submit], .product-form__submit, button[name="add"]'),
    }).catch(function (error) {
      console.error('[CosmeauV6Cart]', error);
    });
  }

  function bindProductForm(form) {
    if (!form || form.dataset[BOUND] === 'true') return;
    form.dataset[BOUND] = 'true';
    form
      .querySelectorAll('[type="submit"][name="add"], [data-atc], [data-v6wp-submit], .product-form__submit')
      .forEach(function (button) {
        if (button.dataset.cv6NativeAtcButtonBound === 'true') return;
        button.dataset.cv6NativeAtcButtonBound = 'true';
        button.addEventListener(
          'click',
          function (event) {
            if (form.matches('[data-cv6-g6-product-form], form[data-cv6-skip-corner-bind]')) return;
            event.preventDefault();
            event.stopPropagation();
            if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
            addFromForm(form, { button }).catch(function (error) {
              console.error('[CosmeauV6Cart]', error);
            });
          },
          true
        );
      });
    form.addEventListener(
      'submit',
      function (event) {
        onSubmit(event);
      },
      false
    );
  }

  function bindAllProductForms(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope
      .querySelectorAll(
        'form[data-type="add-to-cart-form"], form[data-product-form], form.cv6-default-product-form, form.v6wp-hero__form'
      )
      .forEach(bindProductForm);
  }

  function onNativeAtcClick(event) {
    const drawer = document.querySelector('cart-drawer[data-cosmeau-cart]');
    if (!drawer || typeof drawer.open !== 'function') return;
    const button = event.target?.closest?.(
      'button[type="submit"], button[name="add"], [data-atc], [data-v6wp-submit], .product-form__submit'
    );
    const form = button?.form || button?.closest?.('form');
    if (!form || form.matches('[data-cv6-g6-product-form], form[data-cv6-skip-corner-bind]')) return;
    if (!form.querySelector('[name="id"]')) return;

    // Some product shells call form.submit() directly from their click handler.
    // Capture the click before those handlers so native DEV carts never leave the PDP.
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    addFromForm(form, { button }).catch(function (error) {
      console.error('[CosmeauV6Cart]', error);
    });
  }

  if (!window.__cv6NativeAtcCapture) {
    window.__cv6NativeAtcCapture = true;
    document.addEventListener('click', onNativeAtcClick, true);
  }

  // Several PDP shells stop the event during document capture. Window capture
  // runs one level earlier and guarantees one native drawer mutation path.
  if (!window.__cv6NativeAtcWindowCapture) {
    window.__cv6NativeAtcWindowCapture = true;
    window.addEventListener('click', onNativeAtcClick, true);
  }

  // Prime campaign discounts from the click itself because Corner Cart may
  // consume the event before the browser emits a native form submit.
  if (!window.__cv6CampaignClickCapture) {
    window.__cv6CampaignClickCapture = true;
    document.addEventListener('click', primeCampaignOnAtcClick, true);
  }

  // Global capture once.
  if (!window.__cv6CornerCartCapture) {
    window.__cv6CornerCartCapture = true;
    document.addEventListener('submit', onSubmit, true);
  }

  function boot() {
    bindAllProductForms(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  // Late app/section mounts (Shopify section rendering, DealEasy, etc.)
  setTimeout(boot, 0);
  setTimeout(boot, 800);
  setTimeout(boot, 2500);

  api.open = openCornerCart;
  api.refreshAndOpen = refreshAndOpen;
  api.applyCampaignFromForm = applyCampaignFromForm;
  api.addFromForm = addFromForm;
  api.bindProductForm = bindProductForm;
  api.bindAllProductForms = bindAllProductForms;
  api.isOpen = isCornerOpen;
  api.ensureOpen = ensureOpen;
  api.__ready = true;
})();
